import React from 'react';
import { User, BadgeCheck, XCircle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/common';
import { CandidateAdvancedFilter } from '@/components/jobs/CandidateAdvancedFilter';
import { CandidateTable } from '@/components/jobs/CandidateTable';
import { CandidateFilter } from '@/types/candidates/candidates.types';
import { cn } from '@/lib/utils';

interface RecruiterOverviewTabProps {
  inProgressOffersCount: number;
  hiredOffersCount: number;
  rejectedOffersCount: number;
  candidateFilters: Partial<CandidateFilter>;
  setCandidateFilters: (filters: Partial<CandidateFilter>) => void;
  setCurrentPage: (page: number) => void;
  loadingOffers: boolean;
  loadingCandidatesProfiles: boolean;
  paginatedCandidates: any[];
  setSelectedDetailCandidate: (candidate: any) => void;
  handleOpenMenu: (e: React.MouseEvent<HTMLButtonElement>, candidateId: string) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  currentPage: number;
  totalPages: number;
  setActiveTab: (tab: any) => void;
}

/**
 * Tab Tổng Quan / Quản Lý Ứng Viên (`overview`)
 * Bao gồm các thẻ thống kê tổng quan ứng viên (Đang trong quy trình, Đã tuyển, Đã từ chối), bộ lọc nâng cao và bảng hiển thị ứng viên ứng tuyển.
 */
export const RecruiterOverviewTab: React.FC<RecruiterOverviewTabProps> = ({
  inProgressOffersCount,
  hiredOffersCount,
  rejectedOffersCount,
  candidateFilters,
  setCandidateFilters,
  setCurrentPage,
  loadingOffers,
  loadingCandidatesProfiles,
  paginatedCandidates,
  setSelectedDetailCandidate,
  handleOpenMenu,
  pageSize,
  setPageSize,
  currentPage,
  totalPages,
  setActiveTab,
}) => {
  return (
    <div className="space-y-8 animate-fade-in text-left">
      {/* Khối Tiêu Đề Trang & Nút Tạo Tin Nhanh */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-gray-900 leading-tight">Quản lý ứng viên</h1>
        </div>
        <Button
          onClick={() => setActiveTab('post_job')}
          className="bg-[#f97316] hover:bg-[#e04700] text-white text-sm px-6 py-2.5 rounded-2xl flex items-center gap-2 shadow-lg shadow-orange-100 transition-all hover:scale-102"
        >
          <Plus size={16} /> Tạo tin tuyển dụng
        </Button>
      </div>

      {/* Danh Sách 3 Thẻ Thống Kê Chỉ Số Ứng Viên (Stat Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {[
          {
            title: 'Đang trong quy trình',
            count: inProgressOffersCount || 0,
            icon: <User size={22} className="text-[#9f15ff]" />,
            bgColor: 'bg-purple-50/40',
          },
          {
            title: 'Đã tuyển',
            count: hiredOffersCount || 0,
            icon: <BadgeCheck size={22} className="text-[#15b33c]" />,
            bgColor: 'bg-emerald-50/40',
          },
          {
            title: 'Đã từ chối',
            count: rejectedOffersCount || 0,
            icon: <XCircle size={22} className="text-[#ef4444]" />,
            bgColor: 'bg-red-50/40',
          },
        ].map((stat, idx) => (
          <div
            key={idx}
            className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow group"
          >
            <div className="space-y-2">
              <span className="text-[11px] text-gray-400 font-bold uppercase tracking-wider block">{stat.title}</span>
              <h3 className="text-3xl font-black text-gray-900">{stat.count}</h3>
            </div>
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner group-hover:scale-105 transition-transform", stat.bgColor)}>
              {stat.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Bộ Lọc Ứng Viên Nâng Cao (Candidate Advanced Filter) */}
      <div className="mb-6">
        <CandidateAdvancedFilter
          onFilter={(filters) => {
            setCandidateFilters(filters);
            setCurrentPage(1);
          }}
          initialFilters={candidateFilters}
        />
      </div>

      {/* Bảng Dữ Liệu Ứng Viên (Candidate Table Component) */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden text-left">
        {loadingOffers || loadingCandidatesProfiles ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Spinner size="md" />
            <p className="text-xs text-gray-400 font-semibold">Đang tải hồ sơ ứng tuyển...</p>
          </div>
        ) : (
          <CandidateTable
            candidates={paginatedCandidates}
            onViewDetail={setSelectedDetailCandidate}
            onActionClick={handleOpenMenu}
            pageSize={pageSize}
            setPageSize={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
          />
        )}
      </div>
    </div>
  );
};

export default RecruiterOverviewTab;
